import { useEffect, useState } from "react";
import axiosInstance from "../utils/axiosConfig";
import {
  Card,
  Table,
  Input,
  Typography,
  Spin,
  Alert,
  App,
  Button,
  Space,
  Modal,
  Form,
  Popconfirm,
} from "antd";
import {
  SearchOutlined,
  ApiOutlined,
  PlusOutlined,
  DeleteOutlined,
  MinusCircleOutlined,
} from "@ant-design/icons";
import DashboardLayout from "./DashboardLayout";
import { useInsideConsole } from "../console/insideConsole";
import { useStepUp } from "../console/useStepUp";
import { apiCatalogApi } from "../console/consoleApi";

const { Title, Text } = Typography;

// Handles both the Console {success,data,error:{message}} envelope and the legacy
// {error|message} shape.
const errMsg = (err, fallback) =>
  err?.response?.data?.error?.message ||
  err?.response?.data?.error ||
  err?.response?.data?.message ||
  err?.message ||
  fallback;

const isCancelled = (err) => err?.message === "STEP_UP_CANCELLED";

export default function ApiCollection() {
  const { message } = App.useApp();
  // Inside the Console this page talks to the Console-native endpoints (Bearer session +
  // RBAC), which are step-up-gated and audit-logged. Standalone (legacy /api-collection
  // admin route) it uses the legacy x-api-key endpoints. UI is identical either way.
  const insideConsole = useInsideConsole();
  const { withStepUp, modal } = useStepUp();

  const [apis, setApis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  // Selection (bulk remove) + add/delete busy flags.
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchApis();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchApis = async () => {
    try {
      setLoading(true);
      if (insideConsole) {
        const res = await apiCatalogApi.list();
        setApis(res.data.data.apis);
      } else {
        const res = await axiosInstance.get("/api/apiCollection");
        setApis(res.data);
      }
    } catch (err) {
      const m = errMsg(err, "Failed to fetch API collection");
      setError(m);
      message.error(m, 4);
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => {
    form.resetFields();
    setAddOpen(true);
  };

  // Add one or more APIs. A single row is just an array of one, so single/multiple
  // share one path. Inside the Console this is step-up-gated + audited.
  const submitAdd = async (values) => {
    const payload = (values.apis || [])
      .map((a) => ({
        name: a.name?.trim(),
        url: a.url?.trim() || a.name?.trim(),
        serverUrl: a.serverUrl?.trim(),
      }))
      .filter((a) => a.name && a.serverUrl);
    if (payload.length === 0) {
      message.error("Add at least one API with a name and server URL.");
      return;
    }
    setSaving(true);
    try {
      if (insideConsole) {
        await withStepUp((token) => apiCatalogApi.add(payload, token));
      } else {
        await axiosInstance.post("/api/apiCollection/add/batch", payload);
      }
      message.success(`Added ${payload.length} API${payload.length > 1 ? "s" : ""}.`);
      setAddOpen(false);
      form.resetFields();
      fetchApis();
    } catch (err) {
      if (!isCancelled(err)) message.error(errMsg(err, "Failed to add APIs"));
    } finally {
      setSaving(false);
    }
  };

  // Remove one (per-row) or many (bulk) by id. Step-up-gated + audited in the Console.
  const deleteByIds = async (ids) => {
    setDeleting(true);
    try {
      if (insideConsole) {
        await withStepUp((token) => apiCatalogApi.remove(ids, token));
      } else {
        await axiosInstance.post("/api/apiCollection/delete/batch", { ids });
      }
      message.success(`Removed ${ids.length} API${ids.length > 1 ? "s" : ""}.`);
      setSelectedRowKeys((prev) => prev.filter((k) => !ids.includes(k)));
      fetchApis();
    } catch (err) {
      if (!isCancelled(err)) message.error(errMsg(err, "Failed to remove APIs"));
    } finally {
      setDeleting(false);
    }
  };

  const filteredApis = apis.filter(
    (api) =>
      (api.name?.toLowerCase() || "").includes(search.toLowerCase()) ||
      (api.serverUrl?.toLowerCase() || "").includes(search.toLowerCase())
  );

  const columns = [
    {
      title: "API ID",
      dataIndex: "id",
      key: "id",
      sorter: (a, b) => a.id - b.id,
      width: 100,
    },
    {
      title: "API Name",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
    },
    {
      title: "API URL",
      dataIndex: "serverUrl",
      key: "serverUrl",
      ellipsis: true,
      render: (text) => (
        <span style={{ fontFamily: "monospace", fontSize: "13px" }}>{text}</span>
      ),
    },
    {
      title: "",
      key: "actions",
      width: 90,
      align: "right",
      render: (_, row) => (
        <Popconfirm
          title="Remove this API?"
          description={row.name}
          okText="Remove"
          okButtonProps={{ danger: true }}
          onConfirm={() => deleteByIds([row.id])}
        >
          <Button size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  return (
    <DashboardLayout title="API Collection">
      <Card
        style={{
          borderRadius: 12,
          boxShadow: "0 4px 16px rgba(0, 102, 255, 0.08)",
          border: "1px solid #e1e8f0",
        }}
      >
        <div style={{ marginBottom: "24px" }}>
          <Title
            level={2}
            style={{
              textAlign: "center",
              marginBottom: "24px",
              background: "linear-gradient(135deg, #0066ff 0%, #0052cc 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              fontWeight: 700,
            }}
          >
            <ApiOutlined /> API Collection
          </Title>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <Input
              placeholder="Search by API name or URL"
              prefix={<SearchOutlined />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ maxWidth: "400px" }}
              allowClear
              size="large"
            />
            <Space>
              {selectedRowKeys.length > 0 && (
                <Popconfirm
                  title={`Remove ${selectedRowKeys.length} selected API${
                    selectedRowKeys.length > 1 ? "s" : ""
                  }?`}
                  okText="Remove"
                  okButtonProps={{ danger: true }}
                  onConfirm={() => deleteByIds(selectedRowKeys)}
                >
                  <Button danger icon={<DeleteOutlined />} loading={deleting}>
                    Remove selected ({selectedRowKeys.length})
                  </Button>
                </Popconfirm>
              )}
              <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>
                Add APIs
              </Button>
            </Space>
          </div>
        </div>

        {error && (
          <Alert
            title="Error"
            description={error}
            type="error"
            showIcon
            closable
            onClose={() => setError("")}
            style={{ marginBottom: "24px", borderRadius: 8 }}
          />
        )}

        <Spin spinning={loading}>
          <Table
            dataSource={filteredApis}
            columns={columns}
            rowKey="id"
            rowSelection={{
              selectedRowKeys,
              onChange: setSelectedRowKeys,
            }}
            pagination={{
              // defaultPageSize (not pageSize) keeps it uncontrolled so the size changer
              // actually applies — a static pageSize pins it and the dropdown snaps back.
              defaultPageSize: 10,
              showSizeChanger: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} APIs`,
              pageSizeOptions: ["10", "20", "50", "100"],
            }}
            locale={{
              emptyText: "No APIs found",
            }}
          />
        </Spin>
      </Card>

      <Modal
        title="Add APIs"
        open={addOpen}
        onCancel={() => setAddOpen(false)}
        onOk={() => form.submit()}
        okText="Add"
        confirmLoading={saving}
        destroyOnHidden
        width={760}
      >
        <Text type="secondary">
          Add a single API or several at once. Name and Server URL are required and must be
          unique.
        </Text>
        <Form
          form={form}
          layout="vertical"
          onFinish={submitAdd}
          requiredMark={false}
          style={{ marginTop: 16 }}
          initialValues={{ apis: [{}] }}
        >
          <Form.List name="apis">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...rest }) => (
                  <div
                    key={key}
                    style={{
                      border: "1px solid #f0f0f0",
                      borderRadius: 8,
                      padding: "12px 12px 0",
                      marginBottom: 12,
                      position: "relative",
                    }}
                  >
                    <Space
                      style={{ display: "flex", width: "100%" }}
                      align="baseline"
                      styles={{ item: { flex: 1 } }}
                    >
                      <Form.Item
                        {...rest}
                        name={[name, "name"]}
                        label="Name"
                        rules={[{ required: true, message: "Required" }]}
                      >
                        <Input placeholder="GET:/verifyPan" autoComplete="off" />
                      </Form.Item>
                      <Form.Item {...rest} name={[name, "url"]} label="URL (optional)">
                        <Input placeholder="/verifyPan" autoComplete="off" />
                      </Form.Item>
                      <Form.Item
                        {...rest}
                        name={[name, "serverUrl"]}
                        label="Server URL"
                        rules={[{ required: true, message: "Required" }]}
                      >
                        <Input
                          placeholder="https://preprod-api.verifyu.ai/verifyPan"
                          autoComplete="off"
                        />
                      </Form.Item>
                      {fields.length > 1 && (
                        <MinusCircleOutlined
                          onClick={() => remove(name)}
                          style={{ color: "#ff4d4f", fontSize: 18 }}
                          title="Remove this row"
                        />
                      )}
                    </Space>
                  </div>
                ))}
                <Button
                  type="dashed"
                  onClick={() => add()}
                  icon={<PlusOutlined />}
                  block
                >
                  Add another API
                </Button>
              </>
            )}
          </Form.List>
        </Form>
      </Modal>

      {/* Step-up (re-OTP) modal for add/remove inside the Console */}
      {modal}
    </DashboardLayout>
  );
}
